<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import ApexCharts from "apexcharts"
  import {
    ChevronDown,
    ChevronRight,
    Sparkles,
    Download,
    Loader
  } from "lucide-svelte"

  import InitialNote from "./Options/InitialNote.svelte"
  import ScoreDetails from "./Options/ScoreDetails.svelte"
  import CategoryStatus from "./Options/CategoryStatus.svelte"
  import TrustDetails from "./Options/TrustDetails.svelte"
  import AlignmentDetails from "./Options/AlignmentDetails.svelte"
  import DecisionDetails from "./Options/DecisionDetails.svelte"
  import Alignment from "./Options/Alignment.svelte"
  import DecisionMaking from "./Options/DecisionMaking.svelte"
  import Trust from "./Options/Trust.svelte"
  import WhatToDoNext from "./WhatToDoNext.svelte"
  import FastAttackTable from "./FastAttackTable.svelte"

  let props = $props()
  let { questions } = props
  let response = $state(props.response)

  import alignment from "@images/report/alignment.png"
  import decision from "@images/report/decision.png"
  import trust from "@images/report/trust-icon.png"

  import map from "@images/map.jpg"
  import fast_attack from "@images/fast-attack-logo.png"
  import marc_submarine from "@images/report/marc_submarine.jpg"

  let score: HTMLDivElement = $state(null)
  let snapshot: HTMLDivElement = $state(null)

  let showTrustDetails = $state(false)
  let showAlignmentDetails = $state(false)
  let showDecisionDetails = $state(false)

  let scoreHeading = $derived(() => {
    if (response.score.total < 25) {
      return "CRITICAL"
    } else if (response.score.total < 50) {
      return "LOW"
    } else if (response.score.total < 80) {
      return "EMERGING"
    } else if (response.score.total < 100) {
      return "HIGH"
    } else if (response.score.total == 100) {
      return "ELITE"
    }
  })

  let scoreColor = $derived(() => {
    if (response.score.total < 25) {
      return "#ef4444"
    } else if (response.score.total < 50) {
      return "#fdba74"
    } else if (response.score.total < 80) {
      return "#eab308"
    } else if (response.score.total < 100) {
      return "#10b981"
    } else if (response.score.total == 100) {
      return "#16a34a"
    }
  })

  onMount(() => {
    var score_options = {
      series: [response.score.total],
      colors: [scoreColor()],
      chart: {
        height: 350,
        type: "radialBar",
        offsetY: -10
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          dataLabels: {
            name: {
              fontSize: "16px",
              color: scoreColor(),
              offsetY: 120
            },
            value: {
              offsetY: 76,
              fontSize: "22px",
              color: scoreColor(),
              formatter: function (val) {
                return val + "%"
              }
            }
          }
        }
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "horizontal",
          shadeIntensity: 0.75,
          gradientToColors: [scoreColor()],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      stroke: {
        dashArray: 4
      },
      labels: [scoreHeading()]
    }

    var score_chart = new ApexCharts(score, score_options)
    score_chart.render()

    var options2 = {
      series: [
        response.score.trust,
        response.score.decision,
        response.score.alignment,
        response.score.total
      ],
      chart: {
        height: 390,
        type: "radialBar"
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: "30%",
            background: "transparent",
            image: undefined
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: false
            }
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: "16px",
            formatter: function (seriesName, opts) {
              return (
                seriesName + ":  " + opts.w.globals.series[opts.seriesIndex]
              )
            }
          }
        }
      },
      colors: ["#1ab7ea", "#0084ff", "#39539E", "#0077B5"],
      labels: ["Trust", "Decision Agility", "Alignment", "Total Score"],
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              show: false
            }
          }
        }
      ]
    }

    var chart2 = new ApexCharts(snapshot, options2)
    chart2.render()
  })

  let loadingPdf = $state(false)

  async function downloadPdf() {
    loadingPdf = true
    const url = `/pdf/${response.access_key}`
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = "Fast-Attack-Readiness-Report.pdf" // You can change the filename here
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(downloadUrl)
      loadingPdf = false
    } catch (error) {
      console.error("Error downloading the file", error)
    }
  }
</script>

<div class="bg-gray-50 mb-12">
  <div
    class="w-full bg-blue-950 py-6 max-w-6xl mx-auto flex items-center justify-between gap-x-4 px-6 sm:px-12"
  >
    <div>
      <h2
        class="text-xl font-semibold tracking-tight text-gray-100 sm:text-4xl flex items-center"
      >
        Fast Attack Readiness Report
      </h2>
    </div>
    <img src={fast_attack} class="w-40 h-auto" alt="Fast Attack Logo" />
  </div>
  <div
    class="px-4 lg:px-0 py-8 lg:py-12 mx-auto max-w-6xl bg-white rounded shadow-sm"
  >
    <div class="px-2 lg:px-8">
      <div class="mx-auto max-w-4xl">
        <p
          class="text-xl font-medium text-pretty text-blue-950 text-left sm:text-3xl/8"
        >
          How Ready Is Your Leadership Team to Navigate Disruption?
        </p>
        <p
          class="mt-6 text-lg italic text-left font-medium text-pretty text-gray-700 sm:text-xl/8"
        >
          Based on your results from your Readiness Score Quiz
        </p>

        <div
          class="max-w-sm mx-auto h-1 bg-blue-900 rounded mt-4 lg:mt-8"
        ></div>
      </div>
    </div>

    <div
      class="border-l-4 border-gray-200 bg-gray-50 px-4 py-3 sm:py-6 max-w-4xl mx-auto mt-6"
    >
      <InitialNote score={response.score} />
    </div>

    <div class="py-6 flex items-center justify-center">
      {#if loadingPdf}
        <span
          class="py-3 px-6 bg-yellow-50 text-yellow-800 font-semibold rounded-sm shadow text-xl flex items-center"
        >
          <Loader class="size-7 animate-spin mr-4" /> Please wait as we create your
          PDF Report...</span
        >
      {:else}
        <button
          onclick={() => {
            downloadPdf()
          }}
          class="mx-auto inline-flex items-center rounded-md bg-blue-600 px-6 py-2.5 text-base font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
        >
          <Download class="size-5 pr-1" />Download PDF Version of this Report
        </button>
      {/if}
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 mt-12 max-w-4xl mx-auto">
      <div class="bg-blue-900 p-6 rounded-sm">
        <h2
          class="text-2xl font-semibold tracking-tight text-pretty text-gray-100 sm:text-3xl"
        >
          Your readiness score is...
        </h2>

        <div class="flex justify-start items-center w-full mt-6">
          <div class="w-full" bind:this={score}></div>
        </div>

        <div class="max-w-xs mx-auto h-1 bg-blue-50 rounded"></div>
        <div class="mt-6 max-w-56 mx-auto grid grid-cols-2 gap-1">
          <div>
            <p class="font-medium text-base text-gray-200">Below 25%</p>
          </div>
          <div>
            <p class="font-bold text-base text-red-400">CRITICAL</p>
          </div>
          <div>
            <p class="font-medium text-base text-gray-200">25 - 50%</p>
          </div>
          <div>
            <p class="font-bold text-base text-orange-200">LOW</p>
          </div>

          <div>
            <p class="font-medium text-base text-gray-200">50 - 80%</p>
          </div>
          <div>
            <p class="font-bold text-base text-yellow-500">EMERGING</p>
          </div>

          <div>
            <p class="font-medium text-base text-gray-200">80 - 99%</p>
          </div>
          <div>
            <p class="font-bold text-base text-emerald-300">HIGH</p>
          </div>

          <div>
            <p class="font-bold text-base text-gray-100">100%</p>
          </div>
          <div>
            <p class="font-bold text-base text-green-500">ELITE</p>
          </div>
        </div>
      </div>

      <div class="py-6 px-2 lg:px-8">
        <ScoreDetails score={response.score} />
      </div>
    </div>

    <div
      class="mx-auto max-w-2xl lg:max-w-4xl bg-gray-50 rounded shadow-sm mt-6 lg:mt-12 border-l-4 border-blue-800"
    >
      <div class="overflow-hidden py-4">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div
            class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2"
          >
            <div class="lg:pt-4 lg:pr-8">
              <div class="lg:max-w-lg">
                <p
                  class="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-3xl"
                >
                  What Your Score Means
                </p>
                <p class="mt-4 sm:mt-6 text-base sm:text-lg/8 text-gray-700">
                  Keep reading for a detailed breakdown of your score, including
                  key actions steps you can take to improve your team's
                  readiness.
                </p>
                <dl
                  class="mt-5 sm:mt-8 max-w-xl space-y-6 text-base/7 text-gray-600 lg:max-w-none"
                >
                  <div class="relative pl-9">
                    <dt class="inline font-semibold text-gray-900">
                      <Sparkles
                        class="absolute top-1 left-1 size-5 text-blue-600 "
                      />
                      Free 30-Minute Debrief
                    </dt>
                    <dd class="inline">
                      Additionally, Marc Koehler is available for a 30-minute
                      Readiness Debrief where he can unpack your score and
                      create a personalized action plan for your team.
                      <span class="font-bold italic text-sm"
                        >You will also recieve an exclusive coupon code for a
                        discount on the alignment test.</span
                      >
                    </dd>
                  </div>
                </dl>
                <a
                  href="https://calendly.com/marckoehler/readiness-check"
                  target="_blank"
                  class="mt-6 inline-flex items-center rounded-md bg-blue-600 px-6 py-2.5 text-base font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                  >Schedule My Readiness Debrief</a
                >
              </div>
            </div>

            <img
              width="2432"
              height="1442"
              src={map}
              alt="Product screenshot"
              class="w-3xl max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-228 md:-ml-4 lg:-ml-0"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-sm mx-auto h-1 bg-blue-800 rounded mt-8 lg:mt-12"></div>

    <div class="mx-auto max-w-2xl px-2 lg:max-w-4xl lg:px-0 py-12">
      <h2
        class="text-xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-4xl"
      >
        Readiness Snapshot
      </h2>

      <p class="mt-6 text-base sm:text-xl/8 text-gray-700">
        Your Readiness Score is determined from how you scored in the key areas
        of <span class="font-bold">Trust</span>,
        <span class="font-bold">Decision Agility</span>
        and <span class="font-bold">Alignment</span>. Each of the 15 questions
        from the quiz are mapped to one of these areas, and your results reflect
        your strength in each.
      </p>

      <p class="mt-6 text-base sm:text-xl/8 text-gray-700">
        Here is an overview of how you scored in each area, from which we
        calculate your total readiness score.
      </p>

      <div class="w-full mx-2" bind:this={snapshot}></div>

      <div
        class="mt-4 sm:mt-8 divide-y divide-gray-300 overflow-hidden bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-xl"
      >
        <button
          class="relative grid grid-cols-2 gap-x-3 hover:bg-gray-50 w-full text-left"
          class:bg-gray-100={showTrustDetails}
          onclick={() => (showTrustDetails = !showTrustDetails)}
        >
          <div class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6">
            <div class="w-5 md:w-7">
              {#if showTrustDetails}
                <ChevronDown class="size-5 md:size-7" />
              {:else}
                <ChevronRight class="size-5 md:size-7" />
              {/if}
            </div>
            <img
              src={trust}
              alt=""
              class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
            />
            <div class="min-w-0 flex-auto">
              <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
                Trust
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
            <div
              class="flex justify-end md:justify-start items-center gap-x-3 px-3"
            >
              <div class="flex flex-col">
                <p
                  class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
                >
                  STATUS
                </p>
                <CategoryStatus score={response.score.trust} />
              </div>
            </div>
            <div class="flex justify-end items-center gap-x-4 px-3">
              <div class="sm:flex sm:flex-col sm:items-end">
                <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                  YOUR SCORE
                </p>
                <p
                  class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
                >
                  {response.score.trust}%
                </p>
              </div>
            </div>
          </div>

          {#if showTrustDetails}
            <div
              transition:slide={{ duration: 300 }}
              class="col-span-2 text-left bg-white"
            >
              <TrustDetails />
            </div>
          {/if}
        </button>
        <button
          class="relative grid grid-cols-2 gap-x-3 hover:bg-gray-50 w-full text-left"
          class:bg-gray-100={showAlignmentDetails}
          onclick={() => (showAlignmentDetails = !showAlignmentDetails)}
        >
          <div class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6">
            <div class="w-5 md:w-7">
              {#if showAlignmentDetails}
                <ChevronDown class="size-5 md:size-7" />
              {:else}
                <ChevronRight class="size-5 md:size-7" />
              {/if}
            </div>
            <img
              src={alignment}
              alt=""
              class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
            />
            <div class="min-w-0 flex-auto">
              <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
                Alignment
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
            <div
              class="flex justify-end md:justify-start items-center gap-x-3 px-3"
            >
              <div class="flex flex-col">
                <p
                  class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
                >
                  STATUS
                </p>
                <CategoryStatus score={response.score.alignment} />
              </div>
            </div>
            <div class="flex justify-end items-center gap-x-4 px-4">
              <div class="sm:flex sm:flex-col sm:items-end">
                <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                  YOUR SCORE
                </p>
                <p
                  class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
                >
                  {response.score.alignment}%
                </p>
              </div>
            </div>
          </div>
          {#if showAlignmentDetails}
            <div
              transition:slide={{ duration: 300 }}
              class="col-span-2 text-left bg-white"
            >
              <AlignmentDetails />
            </div>
          {/if}
        </button>
        <button
          class="relative grid grid-cols-2 gap-x-3 hover:bg-gray-50 w-full text-left"
          class:bg-gray-100={showDecisionDetails}
          onclick={() => (showDecisionDetails = !showDecisionDetails)}
        >
          <div class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6">
            <div class="w-5 md:w-7">
              {#if showDecisionDetails}
                <ChevronDown class="size-5 md:size-7" />
              {:else}
                <ChevronRight class="size-5 md:size-7" />
              {/if}
            </div>
            <img
              src={decision}
              alt=""
              class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
            />
            <div class="min-w-0 flex-auto">
              <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
                Decision-Making
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
            <div
              class="flex justify-end md:justify-start items-center gap-x-3 px-3"
            >
              <div class="flex flex-col">
                <p
                  class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
                >
                  STATUS
                </p>
                <CategoryStatus score={response.score.decision} />
              </div>
            </div>
            <div class="flex justify-end items-center gap-x-4 px-4">
              <div class="sm:flex sm:flex-col sm:items-end">
                <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                  YOUR SCORE
                </p>
                <p
                  class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
                >
                  {response.score.decision}%
                </p>
              </div>
            </div>
          </div>

          {#if showDecisionDetails}
            <div
              transition:slide={{ duration: 300 }}
              class="col-span-2 text-left bg-white"
            >
              <DecisionDetails />
            </div>
          {/if}
        </button>
      </div>

      <div class="py-12">
        <div class="max-w-xs mx-auto h-1 bg-blue-800 rounded"></div>
      </div>

      <h2
        class="text-2xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-4xl"
      >
        {#if response.score.total < 25}
          Where You’re Breaking Down
        {:else if response.score.total < 50}
          Where You’re Losing Traction
        {:else if response.score.total < 80}
          Where You Can Improve
        {:else if response.score.total < 100}
          Where You’re Still Vulnerable
        {:else}
          Where You Still Have Risk
        {/if}
      </h2>

      <Alignment {questions} {response} />

      <DecisionMaking {questions} {response} />

      <Trust {questions} {response} />
    </div>

    <div class="max-w-sm mx-auto h-1 bg-blue-800 rounded mt-4 lg:mt-8"></div>

    <div class="overflow-hidden bg-white py-8 sm:py-16">
      <div class="mx-auto max-w-2xl px-2 lg:max-w-5xl lg:px-8">
        <div class="max-w-4xl">
          <h1
            class="mt-2 text-3xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl"
          >
            What Fast Attack Leadership Looks Like
          </h1>
        </div>

        <FastAttackTable />

        <section
          class="mt-8 md:mt-16 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-8"
        >
          <div>
            <p
              class="text-base sm:text-xl/8 text-balance text-gray-700 max-w-3xl"
            >
              From 400 feet under the ocean to boardrooms facing market
              volatility, Marc Koehler built the Fast Attack Leadership
              Framework to deliver clarity, autonomy, and speed - in high-stakes
              environments.
            </p>
            <h2
              class="mt-4 sm:mt-8 text-lg sm:text-2xl font-semibold tracking-tight text-pretty text-gray-900"
            >
              Here's what ready teams do differently:
            </h2>
            <ul class="list-disc ml-8">
              <li class="mt-2 sm:mt-4 text-base/7 text-gray-600">
                Act with precision - even under pressure
              </li>
              <li class="mt-2 sm:mt-4 text-base/7 text-gray-600">
                Make distributed decisions at speed
              </li>
              <li class="mt-2 sm:mt-4 text-base/7 text-gray-600">
                Know the mission, live the values, and own their lane
              </li>
              <li class="mt-2 sm:mt-4 text-base/7 text-gray-600">
                Rally fast when the environment shifts
              </li>
              <li class="mt-2 sm:mt-4 text-base/7 text-gray-600">
                Hold each other accountable with trust and transparency
              </li>
            </ul>
          </div>
          <div class="py-4 sm:py-0">
            <div
              class="aspect-square overflow-hidden rounded-xl shadow-xl outline-1 -outline-offset-1 outline-black/10"
            >
              <img
                src={marc_submarine}
                alt=""
                class="block size-full object-cover object-top"
              />
            </div>
          </div>
        </section>
      </div>
    </div>

    <div class="relative bg-white pt-6 sm:pt-16">
      <div class="mx-auto max-w-5xl lg:px-8">
        <h1
          class="text-3xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl"
        >
          What to Do Next
        </h1>

        <WhatToDoNext />

        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 mt-6 sm:mt-8"
        >
          <div class="bg-blue-50 rounded shadow">
            <p
              class="text-lg/8 md:text-2xl/8 font-bold text-gray-100 bg-blue-800 py-2 px-4 rounded-t"
            >
              Take the Fast Attack Alignment Test
            </p>
            <div class="py-2 md:py-4 px-4 md:px-8">
              <p class="mt-3 text-base/7 text-gray-600">
                This team-based diagnostic is the next step after your personal
                Readiness Quiz. It measures how aligned (or misaligned) your
                leadership team really is - across strategy, communication, and
                decision-making.
              </p>

              <div class="mt-6 flex justify-center sm:justify-start">
                <a
                  href="https://app.leadwithpurpose.com/alignment"
                  target="_blank"
                  class="rounded-md bg-blue-700 px-6 py-2.5 text-base font-semibold text-white shadow-xs hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >Take the Test</a
                >
              </div>

              <p class="mt-6 sm:mt-8">
                <span
                  ><strong class="font-semibold text-gray-900"
                    >Why it matters:</strong
                  > If you’re carrying your team on your back, or if alignment seems
                  fine until it’s not, the Fast Attack Alignment Test will reveal
                  the real story.</span
                >
              </p>
              <p class="mt-4 italic text-sm">
                This test is used by elite leadership teams navigating
                high-stakes growth, transitions, and turnarounds.
              </p>
            </div>
          </div>

          <div class="border border-teal-200 rounded shadow">
            <h2
              class="text-xl sm:text-2xl font-bold tracking-tight text-black bg-teal-50 py-2 px-4 rounded-t"
            >
              Or...Talk to Marc Directly
            </h2>
            <div class="py-4 sm:py-6 px-4 md:px-8">
              <p class="mt-2">
                Marc Koehler has trained thousands of leaders - from submarine
                crews to Fortune 500 teams - in how to build agile, aligned
                organizations that thrive under pressure.
              </p>

              <a
                href="https://calendly.com/marckoehler/readiness-check"
                target="_blank"
                class="mt-4 sm:mt-8 text-base sm:text-lg font-bold block text-blue-800 hover:text-blue-600 hover:underline"
                >→ Book a 30-minute Readiness Debrief
              </a>
              <p class="mt-4 sm:mt-6">
                Let’s unpack your score, explore your challenges, and create a
                next step tailored to your team’s stage.
                <span class="font-bold italic text-sm"
                  >You will also receive an exclusive coupon code for a discount
                  on the alignment test.</span
                >
              </p>
              <div
                class="mt-6 sm:mt-10 flex justify-center sm:justify-start mb-2"
              >
                <a
                  href="https://calendly.com/marckoehler/readiness-check"
                  target="_blank"
                  class="rounded-md bg-blue-700 px-6 py-2.5 text-base font-semibold text-white shadow-xs hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >Schedule My Free Readiness Debrief</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-8 sm:py-12">
      <div class="max-w-xs mx-auto h-1 bg-blue-800 rounded"></div>
    </div>

    <div class="relative bg-white py-2 sm:py-8">
      <div class="mx-auto max-w-4xl lg:px-8">
        <h1
          class="text-2xl font-semibold tracking-tight text-gray-900 sm:text-5xl"
        >
          Final Word
        </h1>

        <p class="mt-6 text-lg sm:text-2xl/8 text-gray-700">
          The teams that win in today’s world aren’t the ones with the most
          strategy decks. They’re the ones ready to execute.
        </p>

        <p
          class="mt-4 sm:mt-8 text-base sm:text-2xl/8 text-gray-800 bg-sky-100 px-4 sm:px-6 py-2 sm:py-3 italic rounded"
        >
          You don’t need another leadership offsite. You need a new system.
        </p>
        <p class="mt-6 sm:mt-10 text-2xl sm:text-4xl/8 text-gray-800">
          Let’s build it together.
        </p>
      </div>
    </div>
  </div>
</div>
